import App from '@/App.tsx'
import { AppProvider } from '@/context/AppContext'
import { createHead, UnheadProvider } from '@unhead/react/server'
import { renderToString } from 'react-dom/server'
import { StaticRouter } from 'react-router-dom/server'

interface PrerenderData {
  url: string
}

type PrerenderResult = string | {
  html?: string
  head?: {
    lang?: string
    title?: string
    elements?: Set<{
      type: string
      props: Record<string, string>
      children?: string
    }>
  }
  links?: Set<string> | string[]
} | null | undefined

const paths = [
  '/',
  '/dashboard',
  '/cadastros',
  '/clientes',
  '/obras',
  '/fornecedores',
  '/categorias',
  '/categorias-orcamentarias',
  '/colaboradores',
  '/orcamento',
  '/requisicoes',
  '/cotacoes',
  '/ordens-compra',
  '/contas-pagar',
  '/pagamentos',
  '/aprovacoes',
  '/auditoria',
  '/usuarios',
]

export async function prerender(data: PrerenderData): Promise<PrerenderResult> {
  try {
    const head = createHead({
      disableDefaults: true,
    })

    const html = renderToString(
      <UnheadProvider value={head}>
        <StaticRouter location={data.url}>
          <AppProvider>
            <App />
          </AppProvider>
        </StaticRouter>
      </UnheadProvider>,
    )

    const { parseLinks } = await import('vite-prerender-plugin/parse')
    const links = [...new Set([...parseLinks(html), ...paths])]

    const headTags = await head.resolveTags()
    const lang = headTags.find(tag => tag.tag === 'htmlAttrs')?.props.lang
    const title = headTags.find(tag => tag.tag === 'title')?.textContent
    const elements = headTags
      .filter(tag => tag.tag !== 'htmlAttrs' && tag.tag !== 'title')
      .map(tag => ({
        type: tag.tag,
        props: tag.props,
        children: tag.textContent,
      }))

    return {
      html,
      links,
      head: {
        lang,
        title,
        elements: new Set(elements),
      },
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    console.warn(`Failed to prerender "${data.url}":`, message)
    return null
  }
}

